import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { updateLeadPipelineStage } from '../api/leadPipelineStages';
import { resolveProblemDetailsMessage } from '../../../../lib/api/problemMessage';
import type { ProblemDetails } from '../../../../lib/api/types';
import type { LeadPipelineStageFormValues } from '../types';

function getErrorDetail(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const axiosErr = err as { response?: { data?: ProblemDetails } };
    const data = axiosErr.response?.data;
    if (data) {
      return resolveProblemDetailsMessage(data, fallback);
    }
  }
  return fallback;
}

export function useUpdateLeadPipelineStage(stageId: number | null, onSuccess?: () => void) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LeadPipelineStageFormValues) => {
      if (stageId === null) {
        throw new Error('Stage id is required');
      }
      return updateLeadPipelineStage(stageId, data);
    },
    onSuccess: () => {
      notification.success({ message: 'Pipeline stage updated successfully' });
      void queryClient.invalidateQueries({ queryKey: ['settings-lead-pipeline-stages'] });
      void queryClient.invalidateQueries({ queryKey: ['lead-pipeline'] });
      onSuccess?.();
    },
    onError: (error) => {
      notification.error({ message: getErrorDetail(error, 'Failed to update pipeline stage') });
    },
  });
}
